import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { openRightClickMenu, setSelectedId, updateElement } from "@/store/slideEditorSlice";
import { colors } from "@ai-teach/config";
import type { SlideElement } from "@ai-teach/types";
import { useRef } from "react";
import Moveable from "react-moveable";
import { AudioPlayerRenderer } from "./renderers/AudioPlayerRenderer";
import { ImageRenderer } from "./renderers/ImageRenderer";
import { PhonicRenderer } from "./renderers/PhonicRenderer";
import { ShapeRenderer } from "./renderers/ShapeRenderer";
import { TextRenderer } from "./renderers/TextRenderer";
import { VideoRenderer } from "./renderers/VideoRenderer";
import { AnswerImageRenderer } from "./renderers/questions/AnswerImageRenderer";
import { CompositionRenderer } from "./renderers/questions/CompositionRenderer";
import { DragMatchingImageRenderer } from "./renderers/questions/DragMatchingImageRenderer";
import { DragMatchingTextRenderer } from "./renderers/questions/DragMatchingTextRenderer";
import { FillBlanksRenderer } from "./renderers/questions/FillBlanksRenderer";
import { ImageChoiceRenderer } from "./renderers/questions/ImageChoiceRenderer";
import { OddImageOutRenderer } from "./renderers/questions/OddImageOutRenderer";
import { OddTextOutRenderer } from "./renderers/questions/OddTextOutRenderer";
import { OrderImageRenderer } from "./renderers/questions/OrderImageRenderer";
import { OrderTextRenderer } from "./renderers/questions/OrderTextRenderer";
import { SpeakingRenderer } from "./renderers/questions/SpeakingRenderer";
import { SpellingRenderer } from "./renderers/questions/SpellingRenderer";
import { TextChoiceRenderer } from "./renderers/questions/TextChoiceRenderer";

interface DraggableElementProps {
  element: SlideElement;
  isSelected: boolean;
  scale: number;
}

export function DraggableElement({ element, isSelected, scale }: DraggableElementProps) {
  const dispatch = useAppDispatch();
  const isAspectLocked = useAppSelector((state) => state.slideEditor.present.isAspectLocked);
  const targetRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setSelectedId(element.id));
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(setSelectedId(element.id));

    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    dispatch(
      openRightClickMenu({
        position: { x: rect.right + 10, y: rect.top },
        elementId: element.id,
      })
    );
  };

  const renderElement = () => {
    switch (element.type) {
      case "text":
        return <TextRenderer element={element} isSelected={isSelected} />;
      case "shape":
        return <ShapeRenderer element={element} />;
      case "image":
        return <ImageRenderer element={element} />;
      case "video":
        return <VideoRenderer element={element} />;
      case "audioPlayer":
        return <AudioPlayerRenderer element={element} />;
      case "textChoice":
        return <TextChoiceRenderer element={element} />;
      case "imageChoice":
        return <ImageChoiceRenderer element={element} />;
      case "dragMatchingText":
        return <DragMatchingTextRenderer element={element} />;
      case "dragMatchingImage":
        return <DragMatchingImageRenderer element={element} />;
      case "oddTextOut":
        return <OddTextOutRenderer element={element} />;
      case "oddImageOut":
        return <OddImageOutRenderer element={element} />;
      case "fillBlanks":
        return <FillBlanksRenderer element={element} />;
      case "answerImage":
        return <AnswerImageRenderer element={element} />;
      case "orderImage":
        return <OrderImageRenderer element={element} />;
      case "orderText":
        return <OrderTextRenderer element={element} />;
      case "spelling":
        return <SpellingRenderer element={element} />;
      case "composition":
        return <CompositionRenderer element={element} />;
      case "speaking":
        return <SpeakingRenderer element={element} />;
      case "phonic":
        return <PhonicRenderer element={element} />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Target element — plain absolutely-positioned div */}
      <div
        ref={targetRef}
        style={{
          position: "absolute",
          left: element.position.x,
          top: element.position.y,
          width: element.size.width,
          height: element.size.height,
          transform: `rotate(${element.rotation ?? 0}deg)`,
          zIndex: element.zIndex || 0,
        }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        data-element-id={element.id}
      >
        {/* Element content with its own border/radius */}
        <div
          className="w-full h-full"
          style={{
            border:
              element.border?.stroke &&
              element.border?.lineStyle !== "none" &&
              element.border?.stroke !== "transparent"
                ? `${element.border.strokeWidth ?? 1}px ${element.border.lineStyle ?? "solid"} ${element.border.stroke}`
                : "none",
            borderRadius: element.border.cornerRadius ?? 0,
            boxSizing: "border-box",
          }}
        >
          {renderElement()}
        </div>
      </div>

      {/* Moveable — only mounts when selected, handles drag + resize + rotate */}
      {isSelected && (
        <Moveable
          key={`${element.position.x}-${element.position.y}-${element.size.width}-${element.size.height}-${element.rotation ?? 0}`}
          target={targetRef}
          draggable
          resizable
          rotatable
          keepRatio={isAspectLocked}
          bounds="parent"
          zoom={1 / scale}
          // Don't swallow the mousedown on inputs / contenteditable — lets the field focus & get a caret.
          checkInput
          onDragStart={({ inputEvent }) => {
            const target = inputEvent.target as HTMLElement;
            return !target.closest("[data-no-moveable-drag]");
          }}
          onDrag={({ target, left, top }) => {
            target.style.left = `${left}px`;
            target.style.top = `${top}px`;
          }}
          onDragEnd={({ lastEvent }) => {
            if (lastEvent) {
              dispatch(
                updateElement({
                  id: element.id,
                  position: { x: lastEvent.left, y: lastEvent.top },
                })
              );
            }
          }}
          onResize={({ target, width, height, drag }) => {
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
            target.style.left = `${drag.left}px`;
            target.style.top = `${drag.top}px`;
          }}
          onResizeEnd={({ lastEvent }) => {
            if (lastEvent) {
              dispatch(
                updateElement({
                  id: element.id,
                  position: { x: lastEvent.drag.left, y: lastEvent.drag.top },
                  size: { width: lastEvent.width, height: lastEvent.height },
                })
              );
            }
          }}
          onRotate={({ target, transform }) => {
            target.style.transform = transform;
          }}
          onRotateEnd={({ lastEvent }) => {
            if (lastEvent) {
              dispatch(
                updateElement({
                  id: element.id,
                  rotation: lastEvent.rotation,
                })
              );
            }
          }}
        />
      )}
    </>
  );
}
